/**
 * Coupons (Offers & Promotions) — w-pyms /api/v1/coupons.
 *
 * Listing is shared with customers; create/update/delete are superAdmin only.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Coupon {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  code: string;
  description: string;
  discount_type: 'percentage' | 'flat';
  discount_value: number;
  max_discount: number;
  min_order_amount: number;
  max_usage_total: number;
  max_usage_per_user: number;
  current_usage: number;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applicable_to: string;
}

interface CouponsState {
  coupons: Coupon[];
  loading: boolean;
  error: string | null;
}

const initialState: CouponsState = {
  coupons: [],
  loading: false,
  error: null,
};

const base = `${config.services.pyms}/api/v1/coupons`;

const unwrapList = (p: any): Coupon[] =>
  Array.isArray(p?.data) ? p.data : Array.isArray(p) ? p : [];

export const fetchCoupons = createAsyncThunk(
  'coupons/fetchAll',
  // Admins need expired/inactive coupons too, so the active-only filter is off.
  async (_: void, thunkAPI) => {
    try {
      const response = await apiClient.get(`${base}?active=false&per_page=100`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch coupons');
    }
  }
);

export const createCoupon = createAsyncThunk(
  'coupons/create',
  async (data: Partial<Coupon>, thunkAPI) => {
    try {
      const response = await apiClient.post(base, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create coupon');
    }
  }
);

export const updateCoupon = createAsyncThunk(
  'coupons/update',
  async ({ id, data }: { id: number; data: Partial<Coupon> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${base}/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update coupon');
    }
  }
);

export const deleteCoupon = createAsyncThunk(
  'coupons/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${base}/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete coupon');
    }
  }
);

const couponsSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    clearCouponsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = unwrapList(action.payload);
      })
      .addCase(fetchCoupons.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(createCoupon.rejected, (state, action) => { state.error = action.payload as string; })
      .addCase(updateCoupon.rejected, (state, action) => { state.error = action.payload as string; })

      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.coupons = state.coupons.filter((c) => c.ID !== action.payload);
      })
      .addCase(deleteCoupon.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

export const { clearCouponsError } = couponsSlice.actions;
export default couponsSlice.reducer;
